/**
 * シーンのズーム / パン (Ken Burns) の正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.1-A。
 *
 * 同じ意味・同じ数式の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出し)
 * - video-editor リポジトリの src/schema/motion.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/motion.py (AI 提案の正規化・部分操作の分割)
 *
 * ## 意味
 * - `from` / `to`: 動きの始点・終点の変形。`scale` は 1 = 画面にフィット (拡大なし)。
 *   `x` / `y` は**出力の幅 / 高さに対する % のオフセット**で、0 = 中央・正 = 映像を右 / 下へ動かす
 * - `easing`: 進行に掛けるイージング (既定 `ease-in-out`)
 * - `startSec` / `endSec`: 動く区間 (**そのシーンの表示秒**。テロップ・オーバーレイ・エフェクトと
 *   同じ基準)。**両方省略 = シーン全体**で動く。区間の前は始点の状態で止まり、区間の後は終点の
 *   状態で止まる (「2 秒から 4 秒にかけて寄り、その後は寄ったまま」が作れる)
 * - `window`: **元曲線の評価区間** (既定 `[0, 1]`)。シーンを分割してもズームが巻き戻らないよう、
 *   `from` / `to` / `easing` は元のまま、評価する区間だけを切り分ける (§4.1-A)
 *
 * ## 表示
 * シーン内進行 p (0〜1・**速度適用後の表示尺**に対する割合) を、区間があれば区間内の進行 q に写し
 * (区間の外では 0 / 1 に張り付く)、`u = u0 + (u1 - u0) q` → `easing(u)` で `from` → `to` を
 * 線形補間する (evaluateMotion)。CSS は `translate(x%, y%) scale(s)` (原点は中央) で、transform は
 * 右から適用されるため translate の % は拡大の影響を受けず「出力サイズに対する %」のままになる。
 *
 * ## クランプ
 * 拡大後に画面外の余白が出ないよう、オフセットは各時点の scale から決まる上限
 * `(scale - 1) × 50` (%) に収める。scale 1 (等倍) では移動できない (上限 0)。
 *
 * ## 区間の追従
 * 区間は表示秒なので、トリム / 速度 / 分割の追従はオーバーレイ・エフェクトと同じ規則
 * (schema/timedSpan.ts)。ただし motion は 1 シーンに 1 つで「成立しなくなったら落とす」ではなく、
 * 区間がシーンの外に出たときは**その端の状態で静止する motion** (from = to) に畳む
 * (寄ったまま分割した後半が等倍に戻らないようにするため)。
 */
import { z } from 'zod';
import { isWholeSpan, roundHalfUp } from './timedSpan';
import { formatTimecode } from './timecode';

export const MOTION_EASINGS = ['linear', 'ease-in', 'ease-out', 'ease-in-out'] as const;
export type MotionEasing = (typeof MOTION_EASINGS)[number];

/** 既定のイージング (AI / UI が easing を省略したときの解釈)。 */
export const DEFAULT_MOTION_EASING: MotionEasing = 'ease-in-out';

/** イージングの表示名 (UI のセレクタ)。 */
export const MOTION_EASING_LABELS: Record<MotionEasing, string> = {
  linear: '一定',
  'ease-in': '加速',
  'ease-out': '減速',
  'ease-in-out': 'なめらか',
};

/** scale の許容範囲。1 未満 (縮小) は余白が出るため認めない。 */
export const MOTION_SCALE_MIN = 1;
export const MOTION_SCALE_MAX = 4;
/** x / y (出力サイズに対する %) の許容範囲。実際の上限は scale から決まる (clampMotionOffset)。 */
export const MOTION_OFFSET_LIMIT_PERCENT = 50;

/** プリセットの既定の拡大率 (「倍率」スライダーの初期値)。 */
export const MOTION_PRESET_SCALE = 1.12;
/** UI の「倍率」スライダーの範囲と刻み (AI はスキーマの上限 4 まで書ける。§0-1)。 */
export const MOTION_UI_SCALE_MIN = 1.05;
export const MOTION_UI_SCALE_MAX = 2;
export const MOTION_UI_SCALE_STEP = 0.01;

export const MotionKeyframeSchema = z.object({
  scale: z.number().min(MOTION_SCALE_MIN).max(MOTION_SCALE_MAX),
  x: z.number().min(-MOTION_OFFSET_LIMIT_PERCENT).max(MOTION_OFFSET_LIMIT_PERCENT),
  y: z.number().min(-MOTION_OFFSET_LIMIT_PERCENT).max(MOTION_OFFSET_LIMIT_PERCENT),
});
export type MotionKeyframe = z.infer<typeof MotionKeyframeSchema>;

export const MotionSchema = z.object({
  from: MotionKeyframeSchema,
  to: MotionKeyframeSchema,
  easing: z.enum(MOTION_EASINGS).optional(),
  /** 動く区間 (そのシーンの表示秒)。省略 = シーン全体。 */
  startSec: z.number().nonnegative().optional(),
  endSec: z.number().nonnegative().optional(),
  /** 元曲線の評価区間 [u0, u1] (0 <= u0 < u1 <= 1)。省略 = [0, 1]。分割時に切り分ける。 */
  window: z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)]).optional(),
});
export type Motion = z.infer<typeof MotionSchema>;

/** UI の「動き」セレクタとAI の `set_item_motion {preset}` が共有するプリセット名。 */
export const MOTION_PRESETS = [
  'zoom_in',
  'zoom_out',
  'pan_left',
  'pan_right',
  'pan_up',
  'pan_down',
] as const;
export type MotionPreset = (typeof MOTION_PRESETS)[number];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** 拡大率 scale のときに画面外の余白が出ない、オフセット (%) の絶対値の上限。 */
export function motionOffsetLimit(scale: number): number {
  return Math.max(0, (Math.max(MOTION_SCALE_MIN, scale) - 1) * 50);
}

const IDENTITY_KEYFRAME: MotionKeyframe = { scale: 1, x: 0, y: 0 };

/**
 * プリセットを拡大率 `scale` で組み立てる (正本。backend の build_preset_motion と同値)。
 * - zoom_in: 等倍 → scale (中央)、zoom_out: その逆
 * - pan_*: scale のまま、余白が出ない上限 `(scale - 1) × 50` いっぱいを端から端へ流す
 *   (既定 1.12 倍で ±6%。小数誤差を残さないよう 2 桁に丸める)
 * 区間 / イージングは付けない (呼び出し側が重ねる)。
 */
export function buildPresetMotion(preset: MotionPreset, scale: number = MOTION_PRESET_SCALE): Motion {
  const s = roundHalfUp(clamp(scale, MOTION_SCALE_MIN, MOTION_SCALE_MAX), 4);
  const offset = roundHalfUp(motionOffsetLimit(s), 2);
  const at = (x: number, y: number): MotionKeyframe => ({ scale: s, x, y });
  switch (preset) {
    case 'zoom_in':
      return { from: { ...IDENTITY_KEYFRAME }, to: at(0, 0), easing: DEFAULT_MOTION_EASING };
    case 'zoom_out':
      return { from: at(0, 0), to: { ...IDENTITY_KEYFRAME }, easing: DEFAULT_MOTION_EASING };
    case 'pan_left':
      return { from: at(offset, 0), to: at(-offset, 0), easing: DEFAULT_MOTION_EASING };
    case 'pan_right':
      return { from: at(-offset, 0), to: at(offset, 0), easing: DEFAULT_MOTION_EASING };
    case 'pan_up':
      return { from: at(0, offset), to: at(0, -offset), easing: DEFAULT_MOTION_EASING };
    case 'pan_down':
    default:
      return { from: at(0, -offset), to: at(0, offset), easing: DEFAULT_MOTION_EASING };
  }
}

/**
 * プリセットの既定値 (拡大率 1.12 倍・シーン全体)。backend/services/video_edit/motion.py の
 * MOTION_PRESETS と video-editor/src/schema/motion.ts に同じ値がある。
 */
export const MOTION_PRESET_VALUES: Record<MotionPreset, Motion> = {
  zoom_in: buildPresetMotion('zoom_in'),
  zoom_out: buildPresetMotion('zoom_out'),
  pan_left: buildPresetMotion('pan_left'),
  pan_right: buildPresetMotion('pan_right'),
  pan_up: buildPresetMotion('pan_up'),
  pan_down: buildPresetMotion('pan_down'),
};

/** UI の「動き」セレクタの表示名 (AI への説明にも同じ語を使う)。 */
export const MOTION_PRESET_LABELS: Record<MotionPreset, string> = {
  zoom_in: 'ゆっくり寄る',
  zoom_out: 'ゆっくり引く',
  pan_left: '左へ流す',
  pan_right: '右へ流す',
  pan_up: '上へ流す',
  pan_down: '下へ流す',
};

/**
 * イージング。FE / レンダラー / backend で同じ値になるよう、2 次の単純な式に固定する
 * (CSS の cubic-bezier は Python 側で厳密に再現しにくいため採用しない)。
 */
export function easeMotion(easing: MotionEasing | undefined, u: number): number {
  const t = clamp(u, 0, 1);
  switch (easing ?? DEFAULT_MOTION_EASING) {
    case 'linear':
      return t;
    case 'ease-in':
      return t * t;
    case 'ease-out':
      return 1 - (1 - t) * (1 - t);
    case 'ease-in-out':
    default:
      return t < 0.5 ? 2 * t * t : 1 - 2 * (1 - t) * (1 - t);
  }
}

export interface MotionTransform {
  scale: number;
  /** 出力幅に対する % (正 = 右へ)。 */
  x: number;
  /** 出力高さに対する % (正 = 下へ)。 */
  y: number;
}

// ── 区間 ─────────────────────────────────────────────────────────────────────

export interface MotionSpan {
  startSec: number;
  endSec: number;
}

/** 区間を持つか (シーン全体で動くのではないか)。 */
export function hasMotionWindow(motion: Pick<Motion, 'startSec' | 'endSec'>): boolean {
  return !isWholeSpan(motion);
}

/**
 * 動く区間 (省略 = シーン全体) を表示尺の内側へ収めたもの。
 * 成立しない区間 (end <= start) はシーン全体に倒す (normalizeMotion が保存前に落とすので、
 * ここに来るのは durationSec で詰めた結果だけ)。
 */
export function motionSpan(motion: Pick<Motion, 'startSec' | 'endSec'>, durationSec: number): MotionSpan {
  const total = Math.max(0, durationSec);
  const start = clamp(motion.startSec ?? 0, 0, total);
  const end = clamp(motion.endSec ?? total, 0, total);
  if (!(end > start)) return { startSec: 0, endSec: total };
  return { startSec: start, endSec: end };
}

/**
 * シーン内進行 p (0〜1) を区間内の進行 q (0〜1) に写す。区間の前は 0・後は 1 に張り付く。
 * `durationSec` を省略した / 区間が無いときは p そのもの (従来どおりシーン全体で動く)。
 */
export function motionProgress(motion: Motion, p: number, durationSec?: number): number {
  const pp = clamp(p, 0, 1);
  if (durationSec === undefined || !(durationSec > 0) || !hasMotionWindow(motion)) return pp;
  const span = motionSpan(motion, durationSec);
  const length = span.endSec - span.startSec;
  if (!(length > 0)) return pp;
  const sec = pp * durationSec;
  return clamp((sec - span.startSec) / length, 0, 1);
}

/** 元曲線のパラメータ u (0〜1) における変形 (window・区間を介さない生の曲線)。 */
export function motionCurveAt(motion: Motion, u: number): MotionTransform {
  const e = easeMotion(motion.easing, u);
  const scale = clamp(
    motion.from.scale + (motion.to.scale - motion.from.scale) * e,
    MOTION_SCALE_MIN,
    MOTION_SCALE_MAX,
  );
  const limit = motionOffsetLimit(scale);
  return {
    scale,
    x: clamp(motion.from.x + (motion.to.x - motion.from.x) * e, -limit, limit),
    y: clamp(motion.from.y + (motion.to.y - motion.from.y) * e, -limit, limit),
  };
}

/**
 * シーン内進行 p (0〜1・速度適用後の表示尺に対する割合) における変形を求める。
 * 区間 (startSec / endSec) があれば `durationSec` (シーンの表示尺) で区間内の進行に写し、
 * window で元曲線の評価区間に写してから easing を掛けるため、分割しても同じ時刻で同じ値になる。
 */
export function evaluateMotion(motion: Motion, p: number, durationSec?: number): MotionTransform {
  const [u0, u1] = motion.window ?? [0, 1];
  const q = motionProgress(motion, p, durationSec);
  return motionCurveAt(motion, u0 + (u1 - u0) * q);
}

/**
 * CSS の transform 文字列。translate → scale の順で書く (transform は右から適用されるので
 * 実際には拡大してから移動するのではなく、移動量が拡大の影響を受けない = % が出力サイズ基準になる)。
 */
export function motionTransformCss(t: MotionTransform): string {
  return `translate(${round4(t.x)}%, ${round4(t.y)}%) scale(${round4(t.scale)})`;
}

function round4(value: number): number {
  return Math.round(value * 10000) / 10000;
}

function roundSec(value: number): number {
  return roundHalfUp(value, 2);
}

/**
 * 元曲線のパラメータ u の状態で**静止する** motion (from = to)。等倍・中央なら undefined (動きなし)。
 * 分割 / トリムで区間がシーンの外に出たとき、「寄ったまま」を保つために使う。
 */
export function holdMotionAt(motion: Motion, u: number): Motion | undefined {
  const at = motionCurveAt(motion, clamp(u, 0, 1));
  const keyframe: MotionKeyframe = { scale: round4(at.scale), x: round4(at.x), y: round4(at.y) };
  return normalizeMotion({ from: keyframe, to: { ...keyframe }, easing: motion.easing });
}

/** 静止している (from = to。分割 / トリムで畳まれた「寄ったまま」) か。等倍・中央は含めない。 */
export function isHoldMotion(motion: Motion | undefined | null): boolean {
  if (!motion || isStaticMotion(motion)) return false;
  const same = (a: number, b: number) => Math.abs(a - b) < 1e-6;
  return (
    same(motion.from.scale, motion.to.scale) &&
    same(motion.from.x, motion.to.x) &&
    same(motion.from.y, motion.to.y)
  );
}

/**
 * シーンを表示秒 `atSec` (表示尺 `durationSec`) で分割したときの、前半 / 後半の motion。
 * from / to / easing は変えない (§4.1-A)。
 * - 区間なし (シーン全体): 元曲線の評価区間 window だけを切り分ける (従来どおり)
 * - 区間あり: オーバーレイ / エフェクトと同じ 3 分岐。分割点が区間の前なら前半は始点で静止・
 *   後なら後半は終点で静止 (静止が等倍・中央なら undefined = 動きなし)。区間をまたぐときは
 *   区間と window を同じ割合で切り分ける
 */
export function splitMotion(
  motion: Motion,
  atSec: number,
  durationSec: number,
): { first: Motion | undefined; second: Motion | undefined } {
  const [u0, u1] = motion.window ?? [0, 1];
  const total = Math.max(0, durationSec);
  const at = clamp(atSec, 0, total);
  if (!hasMotionWindow(motion)) {
    const mid = u0 + (u1 - u0) * (total > 0 ? at / total : 0);
    return {
      first: { ...motion, window: [u0, mid] },
      second: { ...motion, window: [mid, u1] },
    };
  }
  const span = motionSpan(motion, total);
  if (at <= span.startSec + 1e-9) {
    const second: Motion = { ...motion };
    const start = roundSec(span.startSec - at);
    if (start > 0) second.startSec = start;
    else delete second.startSec;
    if (motion.endSec !== undefined) second.endSec = roundSec(span.endSec - at);
    return { first: holdMotionAt(motion, u0), second };
  }
  if (at >= span.endSec - 1e-9) {
    return { first: { ...motion }, second: holdMotionAt(motion, u1) };
  }
  const q = (at - span.startSec) / (span.endSec - span.startSec);
  const mid = u0 + (u1 - u0) * q;
  const first: Motion = { ...motion, window: [u0, mid], endSec: roundSec(at) };
  const second: Motion = { ...motion, window: [mid, u1] };
  delete second.startSec;
  if (motion.endSec !== undefined) second.endSec = roundSec(span.endSec - at);
  else delete second.endSec;
  return { first, second };
}

/**
 * 新しい表示尺に合わせて区間を詰める (トリム・速度ランプ / フリーズの切り分け後)。
 * 区間なしはそのまま。区間がシーンの後ろに出てしまったら始点で静止 (等倍なら undefined)、
 * 終わりだけはみ出すなら終わりを詰める (シーン末尾まで = endSec 省略に戻す)。
 */
export function clampMotionToDuration(motion: Motion, durationSec: number): Motion | undefined {
  if (!hasMotionWindow(motion)) return motion;
  const total = Math.max(0, durationSec);
  const start = motion.startSec ?? 0;
  if (start >= total - 1e-9) {
    const [u0] = motion.window ?? [0, 1];
    return holdMotionAt(motion, u0);
  }
  const next: Motion = { ...motion };
  if (next.endSec !== undefined && next.endSec >= total - 1e-9) delete next.endSec;
  return next;
}

/** 速度変更で表示尺が変わったときの区間の詰め直し (区間なしは触らない)。 */
export function rescaleMotionForSpeed(motion: Motion, prevSpeed: number, nextSpeed: number): Motion {
  if (!hasMotionWindow(motion)) return motion;
  if (!(prevSpeed > 0) || !(nextSpeed > 0) || Math.abs(prevSpeed - nextSpeed) < 1e-9) return motion;
  const ratio = prevSpeed / nextSpeed;
  const next: Motion = { ...motion };
  if (motion.startSec !== undefined) next.startSec = roundSec(motion.startSec * ratio);
  if (motion.endSec !== undefined) next.endSec = roundSec(motion.endSec * ratio);
  return next;
}

/** 動きなし (from と to が同じ = 変化しない) か。 */
export function isStaticMotion(motion: Motion | undefined | null): boolean {
  if (!motion) return true;
  const same = (a: number, b: number) => Math.abs(a - b) < 1e-6;
  return (
    same(motion.from.scale, motion.to.scale) &&
    same(motion.from.x, motion.to.x) &&
    same(motion.from.y, motion.to.y) &&
    same(motion.from.scale, 1) &&
    same(motion.from.x, 0) &&
    same(motion.from.y, 0)
  );
}

/**
 * 1 点 (from / to) を保存形に収める。backend の _normalize_keyframe + _clamp_keyframe_offsets と同順:
 * 1. scale を [MOTION_SCALE_MIN, MOTION_SCALE_MAX] へ
 * 2. オフセットを ±MOTION_OFFSET_LIMIT_PERCENT へ
 * 3. さらに、その点の scale で余白が出ない範囲 (motionOffsetLimit) へ
 */
function clampKeyframe(keyframe: MotionKeyframe): MotionKeyframe {
  const scale = clamp(keyframe.scale, MOTION_SCALE_MIN, MOTION_SCALE_MAX);
  const limit = Math.min(motionOffsetLimit(scale), MOTION_OFFSET_LIMIT_PERCENT);
  // 上限 0 に負値をクランプすると JS では -0 になる。Python 側は 0.0 を返すので符号を揃える
  const zeroSafe = (value: number) => (value === 0 ? 0 : value);
  return {
    scale,
    x: zeroSafe(clamp(keyframe.x, -limit, limit)),
    y: zeroSafe(clamp(keyframe.y, -limit, limit)),
  };
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * 外部入力 (AI の提案・古い保存データ) の motion を保存形へ正規化する。
 * **保存値 = 実効値**にするため、表示時と同じクランプ (拡大後に画面外の余白が出ない範囲) を
 * 掛けてから静止判定し、動かない motion は undefined (= 動きなし) に倒す。
 * そうしないと「クランプで一切動かない motion」(例: 等倍のまま x を ±10 に振る) が残り、
 * UI の「カスタム (AI)」表示・カードのバッジ・書き出しの注記だけが出てしまう。
 *
 * 区間 (startSec / endSec) はエフェクトと同じ規則: 0.01 秒に丸め、end <= start は区間として
 * 成立しないので両方落とす (= シーン全体)。start が 0 ならキーを付けない。
 *
 * サーバー側の正本は backend/services/video_edit/motion.py の normalize_motion
 * (AI 提案はそちらを必ず通る)。ここは同じ規則を FE / レンダラーでも持つための実装で、
 * 3 実装が同じ結果になることをそれぞれのテストで担保する。
 */
export function normalizeMotion(motion: Motion | undefined | null): Motion | undefined {
  // from / to を欠いた壊れた入力 (旧データ・AI の書き間違い) でも throw しない
  if (!motion || !motion.from || !motion.to) return undefined;
  const next: Motion = {
    ...motion,
    from: clampKeyframe(motion.from),
    to: clampKeyframe(motion.to),
    // easing は backend の normalize_motion と同じく既定値を埋めて返す (3 実装で出力を一致させる)
    easing: motion.easing ?? DEFAULT_MOTION_EASING,
  };
  delete next.startSec;
  delete next.endSec;
  const startRaw = num(motion.startSec);
  const endRaw = num(motion.endSec);
  const startSec = startRaw !== undefined && startRaw >= 0 ? roundSec(startRaw) : undefined;
  const endSec = endRaw !== undefined && endRaw >= 0 ? roundSec(endRaw) : undefined;
  if (!(endSec !== undefined && endSec <= (startSec ?? 0))) {
    if (startSec !== undefined && startSec > 0) next.startSec = startSec;
    if (endSec !== undefined) next.endSec = endSec;
  }
  return isStaticMotion(next) ? undefined : next;
}

/**
 * motion がどのプリセットに一致するか (一致しなければ null = 「カスタム (AI)」)。
 * **形で判定する** (値そのものではない): 倍率・区間・イージングは UI で変えられるパラメータなので、
 * 1.3 倍の zoom_in も linear の pan_left もプリセット名で表示する。window は分割で変わるため
 * 判定に含めない (分割後の半分も元のプリセット名で表示する)。
 * - zoom_in: 中央のまま等倍 → 拡大、zoom_out: 拡大 → 等倍
 * - pan_*: 拡大率が一定 (> 1) で、片方の軸は 0 のまま、もう片方の軸を対称に (+a → -a) 動かす
 */
export function motionPresetOf(motion: Motion | undefined | null): MotionPreset | null {
  if (!motion || !motion.from || !motion.to) return null;
  const eps = 1e-6;
  const near = (a: number, b: number) => Math.abs(a - b) < eps;
  const { from, to } = motion;
  const centered = near(from.x, 0) && near(from.y, 0) && near(to.x, 0) && near(to.y, 0);
  if (centered) {
    if (near(from.scale, 1) && to.scale > 1 + eps) return 'zoom_in';
    if (near(to.scale, 1) && from.scale > 1 + eps) return 'zoom_out';
    return null;
  }
  if (!near(from.scale, to.scale) || !(from.scale > 1 + eps)) return null;
  if (near(from.y, 0) && near(to.y, 0) && near(from.x, -to.x)) {
    if (from.x > eps) return 'pan_left';
    if (from.x < -eps) return 'pan_right';
  }
  if (near(from.x, 0) && near(to.x, 0) && near(from.y, -to.y)) {
    if (from.y > eps) return 'pan_up';
    if (from.y < -eps) return 'pan_down';
  }
  return null;
}

/** 動きの「倍率」(from / to の大きい方の scale。UI のスライダーの値)。 */
export function motionScaleOf(motion: Motion | undefined | null): number {
  if (!motion || !motion.from || !motion.to) return MOTION_PRESET_SCALE;
  return roundHalfUp(Math.max(motion.from.scale, motion.to.scale), 2);
}

/**
 * UI でプリセット / 倍率を選び直したときの motion。形はプリセットから組み立て直し、
 * 区間・イージングは現在の値を引き継ぐ (倍率を省略したときは現在の倍率も引き継ぐ)。
 */
export function applyMotionPreset(
  current: Motion | undefined,
  preset: MotionPreset,
  scale?: number,
): Motion {
  const nextScale = clamp(
    scale ?? (current ? motionScaleOf(current) : MOTION_PRESET_SCALE),
    MOTION_UI_SCALE_MIN,
    MOTION_UI_SCALE_MAX,
  );
  const built = buildPresetMotion(preset, nextScale);
  if (!current) return built;
  const next: Motion = { ...built, easing: current.easing ?? DEFAULT_MOTION_EASING };
  if (current.startSec !== undefined) next.startSec = current.startSec;
  if (current.endSec !== undefined) next.endSec = current.endSec;
  return normalizeMotion(next) ?? built;
}

/** 「動き」の表示名 (UI のセレクタ・タイムラインのバッジ)。プリセット外は「カスタム (AI)」。 */
export function motionLabel(motion: Motion | undefined | null): string {
  if (!motion) return 'なし';
  const preset = motionPresetOf(motion);
  if (preset) return MOTION_PRESET_LABELS[preset];
  return isHoldMotion(motion) ? '固定 (寄ったまま)' : 'カスタム (AI)';
}

function fmtSec(value: number): string {
  return String(roundHalfUp(value, 2));
}

/**
 * UI のカード / AI の要約に出す 1 行 (「ゆっくり寄る 1.3倍 (2〜4秒)」「左へ流す (0〜1.5秒)・一定」)。
 * 倍率は既定 (1.12) のときは書かない。区間が無ければ「(シーン全体)」。既定でないイージングは末尾に添える。
 * backend の describe_motion と同じ文言。`fps` を渡すと区間を画面向けの "MM:SS:FF" で書く
 * (「ゆっくり寄る (00:00:12〜00:02:00)」)。
 */
export function describeMotion(motion: Motion | undefined | null, fps?: number): string {
  if (!motion) return 'なし';
  const parts: string[] = [motionLabel(motion)];
  const preset = motionPresetOf(motion);
  if (preset) {
    const scale = motionScaleOf(motion);
    if (Math.abs(scale - MOTION_PRESET_SCALE) > 1e-9) parts.push(`${fmtSec(scale)}倍`);
  } else if (!isHoldMotion(motion)) {
    parts.push(
      `拡大 ${fmtSec(motion.from.scale)}→${fmtSec(motion.to.scale)} / `
      + `位置 ${fmtOffset(motion.from.x)},${fmtOffset(motion.from.y)}→`
      + `${fmtOffset(motion.to.x)},${fmtOffset(motion.to.y)}%`,
    );
  } else {
    parts.push(`${fmtSec(motion.from.scale)}倍`);
  }
  if (!isHoldMotion(motion)) {
    const start = motion.startSec ?? 0;
    if (motion.startSec === undefined && motion.endSec === undefined) parts.push('(シーン全体)');
    else if (fps !== undefined) {
      const end = motion.endSec !== undefined ? formatTimecode(motion.endSec, fps) : '';
      parts.push(`(${formatTimecode(start, fps)}〜${end})`);
    } else if (motion.endSec !== undefined) parts.push(`(${fmtSec(start)}〜${fmtSec(motion.endSec)}秒)`);
    else parts.push(`(${fmtSec(start)}秒〜)`);
    const easing = motion.easing ?? DEFAULT_MOTION_EASING;
    if (easing !== DEFAULT_MOTION_EASING) parts.push(`・${MOTION_EASING_LABELS[easing]}`);
  }
  return parts.join(' ').replace(' ・', '・');
}

function fmtOffset(value: number): string {
  const rounded = roundHalfUp(value, 0);
  return rounded > 0 ? `+${rounded}` : String(rounded === 0 ? 0 : rounded);
}
